import type { SCCodeEditorAndFragmentProps } from '../code';
import { SCCodeEditorAndFragment } from '../code';
import { SCFormLabel } from '../form';
import type { SCFormFieldProps } from './form_field';
import { SCFormField } from './form_field';

interface SCFormFieldSCCodeEditorAndFragmentProps
  extends SCFormFieldProps, Omit<SCCodeEditorAndFragmentProps, 'value' | 'onChange'> {
  hasEditor?: boolean;
  maxHeight?: string | number; // Maximum height for the editor
}

function SCFormFieldSCCodeEditorAndFragment({
  label,
  name,
  required,
  disabled,
  rules,
  actionButtons,
  hasEditor = true,
  defaultValue,
  maxHeight = 600,
  ...rest
}: SCFormFieldSCCodeEditorAndFragmentProps) {
  //const resolvedHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  return (
    <SCFormField
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      rules={rules}
      withMarginBottom
      defaultValue={defaultValue}
    >
      {({ field, extraProps }) => {
        // Description: display action buttons as standalone highlighting if hasEditor=false
        const isActionStandalone = !hasEditor;

        const editorLabel = label ? (
          <SCFormLabel
            label={label}
            required={required}
            actionButtons={isActionStandalone || actionButtons}
            // Note: same as Tooltip feature implemented in eone-ui/form-input
            Tooltip={extraProps?.Tooltip}
          />
        ) : undefined;

        return (
          <>
            {hasEditor ? (
              <div
                className="sc-code-editor-container"
                // The editor's height is governed downstream by SCCodeEditorAndFragment's
                // scroll-wrapper, which caps the editor region and gives it its own scrollbar. Do
                // NOT also cap this outer container at `maxHeight` with `overflow: hidden`: the
                // label + padding live inside this box, so capping it pushes the editor's bottom
                // past the cap and clips it — the last lines of a tall query become unreachable.
                // Let the container grow to fit label + editor and let the editor scroll internally.
                // The fixed-height + hidden fallback only applies when no maxHeight is supplied
                // (the editor region is otherwise unbounded).
                style={{
                  height: maxHeight ? undefined : '600px',
                  overflow: maxHeight ? undefined : 'hidden',
                }}
              >
                <SCCodeEditorAndFragment
                  value={field.value}
                  onChange={field.onChange}
                  {...rest}
                  editorTitle={editorLabel}
                  disabled={field.disabled}
                  maxHeight={maxHeight}
                  {...extraProps}
                />
              </div>
            ) : null}
            {isActionStandalone ? (
              <div className="sc-form-field-code-editor-action-standalone">{actionButtons}</div>
            ) : null}
          </>
        );
      }}
    </SCFormField>
  );
}

export { SCFormFieldSCCodeEditorAndFragment };
export type { SCFormFieldSCCodeEditorAndFragmentProps };
